import React from 'react';
import { Link } from 'react-router-dom';
import SiteNav from '../../components/site/SiteNav';
import SiteFooter from '../../components/site/SiteFooter';
import { Building2, Award, Users, TrendingUp, MapPin, Calendar, Quote, ArrowRight, Star } from 'lucide-react';

const getInitials = (name) => {
    const words = name.trim().split(/\s+/);
    if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
};

const References = () => {
    const stats = [
        { icon: Building2, value: '15+', label: 'Aktif İşletme', color: '#6f4e37' },
        { icon: Users, value: '2.500+', label: 'Kayıtlı Müşteri', color: '#0ea5e9' },
        { icon: TrendingUp, value: '8.500+', label: 'Aylık İşlem', color: '#10b981' },
        { icon: MapPin, value: '2', label: 'Aktif Şehir', color: '#f59e0b' },
    ];

    const businesses = [
        { name: 'Kahve Durağı', city: 'Kadıköy, İstanbul', sector: 'Kafe', since: '2026 Q1', color: '#6f4e37' },
        { name: 'Pizzalab', city: 'Beşiktaş, İstanbul', sector: 'Pizza', since: '2026 Q1', color: '#f59e0b' },
        { name: 'Safran Lokantası', city: 'Safranbolu, Karabük', sector: 'Geleneksel Restoran', since: '2026 Q1', color: '#b45309' },
        { name: 'Üçüncü Dalga', city: 'Beyoğlu, İstanbul', sector: 'Specialty Coffee', since: '2026 Q1', color: '#78350f' },
        { name: 'Sokak Lezzetleri', city: 'Üsküdar, İstanbul', sector: 'Fast Casual', since: '2026 Q2', color: '#059669' },
        { name: 'Cheese & Wine', city: 'Nişantaşı, İstanbul', sector: 'Bistro', since: '2026 Q2', color: '#581c87' },
        { name: 'Safran Bahçe', city: 'Safranbolu, Karabük', sector: 'Çay Bahçesi', since: '2026 Q2', color: '#a16207' },
        { name: 'Brunch Lab', city: 'Maslak, İstanbul', sector: 'Brunch', since: '2026 Q2', color: '#ea580c' },
        { name: 'Boğaz Kafe', city: 'Sarıyer, İstanbul', sector: 'Kafe', since: '2026 Q2', color: '#0284c7' },
        { name: 'Tatlı Köşe', city: 'Şişli, İstanbul', sector: 'Pastane', since: '2026 Q2', color: '#ec4899' },
        { name: 'Demirçelik Mangal', city: 'Karabük Merkez', sector: 'Mangal', since: '2026 Q3', color: '#dc2626' },
        { name: 'Artisan Bakery', city: 'Levent, İstanbul', sector: 'Fırın', since: '2026 Q3', color: '#92400e' },
        { name: 'Lokum Cafe', city: 'Safranbolu, Karabük', sector: 'Kafe', since: '2026 Q3', color: '#7c2d12' },
        { name: 'Balık Limanı', city: 'Bakırköy, İstanbul', sector: 'Balık Restoranı', since: '2026 Q3', color: '#0369a1' },
        { name: 'Yayla Sofrası', city: 'Karabük Merkez', sector: 'Ev Yemeği', since: '2026 Q3', color: '#15803d' },
        { name: 'Meze Mahal', city: 'Beyoğlu, İstanbul', sector: 'Meyhane', since: '2026 Q3', color: '#8b5cf6' },
    ];

    const testimonials = [
        {
            quote: 'QR menü ve sadakat sistemini tek yerden yönetmek iş yükümüzü yarıya indirdi. Müşterilerimizin tekrar gelme oranı %40 arttı.',
            author: 'Mehmet Yılmaz',
            role: 'Kurucu, Kahve Durağı',
            rating: 5,
        },
        {
            quote: 'ugra\'nın AI danışmanı sayesinde kampanyalarımızı veriyle şekillendiriyoruz. 3 ayda satışlarımızı %28 artırdık.',
            author: 'Elif Demir',
            role: 'İşletme Sahibi, Safran Lokantası',
            rating: 5,
        },
        {
            quote: 'Kağıt kartları çöpe attık. Hem çevreciyiz hem de müşteri verisine anında erişiyoruz. Kurulum 10 dakikada bitti.',
            author: 'Can Öztürk',
            role: 'Genel Müdür, Pizzalab',
            rating: 5,
        },
    ];

    return (
        <div className="references-page" style={{ background: 'var(--bg-main)', color: 'var(--text-main)', minHeight: '100vh', overflowX: 'hidden' }}>
            <SiteNav />
            <style>{`
                .ref-card { background: var(--bg-card); border: 1px solid var(--border); border-radius: 20px; padding: 1.5rem; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); position: relative; overflow: hidden; }
                .ref-card:hover { border-color: var(--primary); transform: translateY(-4px); box-shadow: 0 20px 40px -15px rgba(111, 78, 55, 0.15); }
                .ref-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, transparent, var(--primary), transparent); opacity: 0; transition: opacity 0.3s; }
                .ref-card:hover::before { opacity: 1; }
                .ref-logo { width: 56px; height: 56px; border-radius: 16px; display: flex; align-items: center; justify-content: center; color: white; font-weight: 900; font-size: 1.25rem; letter-spacing: -0.02em; flex-shrink: 0; transition: transform 0.3s; }
                .ref-card:hover .ref-logo { transform: rotate(-4deg) scale(1.05); }
                .stat-card { background: var(--bg-card); border: 1px solid var(--border); border-radius: 24px; padding: 2rem; text-align: center; transition: all 0.3s; }
                .stat-card:hover { transform: translateY(-2px); border-color: var(--primary); }
                .testimonial-card { background: var(--bg-card); border: 1px solid var(--border); border-radius: 24px; padding: 2.5rem; position: relative; transition: all 0.3s; }
                .testimonial-card:hover { border-color: var(--primary); }
                @media (max-width: 768px) {
                    .references-page section { padding-left: 5% !important; padding-right: 5% !important; }
                    .ref-grid { grid-template-columns: 1fr 1fr !important; gap: 1rem !important; }
                    .ref-card { padding: 1rem !important; }
                    .stat-grid { grid-template-columns: 1fr 1fr !important; gap: 1rem !important; }
                    .testimonial-grid { grid-template-columns: 1fr !important; }
                }
                @media (max-width: 480px) {
                    .ref-grid { grid-template-columns: 1fr !important; }
                }
            `}</style>

            {/* Hero */}
            <section style={{ padding: '14rem 10% 3rem', textAlign: 'center' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(var(--primary-rgb), 0.1)', color: 'var(--primary)', padding: '8px 16px', borderRadius: 99, fontSize: '0.8125rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: '1.5rem' }}>
                    <Award size={14} /> Referanslarımız
                </div>
                <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', fontWeight: 900, letterSpacing: '-0.03em', marginBottom: '1rem' }}>
                    15'ten fazla işletme ugra ile büyüyor
                </h1>
                <p style={{ color: 'var(--text-muted)', fontSize: '1.1rem', maxWidth: 680, margin: '0 auto 2rem' }}>
                    Genç bir ekibiz, hızla büyüyoruz. İstanbul ve Karabük'te müşteri sadakatini, menü yönetimini ve dijital operasyonları ugra ile güçlendiren işletmelerle birlikteyiz.
                </p>
            </section>

            {/* Stats */}
            <section style={{ padding: '0 10% 5rem' }}>
                <div className="stat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem', maxWidth: 1100, margin: '0 auto' }}>
                    {stats.map((s, i) => (
                        <div key={i} className="stat-card">
                            <div style={{ width: 56, height: 56, borderRadius: 16, background: `${s.color}15`, color: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                                <s.icon size={28} />
                            </div>
                            <div style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 900, letterSpacing: '-0.02em', marginBottom: '0.25rem' }}>{s.value}</div>
                            <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem', fontWeight: 600 }}>{s.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Business Grid */}
            <section style={{ padding: '0 10% 5rem' }}>
                <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
                    <h2 style={{ fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', fontWeight: 900, letterSpacing: '-0.02em', marginBottom: '0.75rem' }}>Bize güvenen işletmeler</h2>
                    <p style={{ color: 'var(--text-muted)', fontSize: '1rem' }}>Kafelerden restoranlara, pastanelerden mangala; İstanbul ve Karabük'te her ölçekte işletme ugra'yı seçiyor.</p>
                </div>
                <div className="ref-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1.25rem', maxWidth: 1200, margin: '0 auto' }}>
                    {businesses.map((b, i) => (
                        <div key={i} className="ref-card">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: '1rem' }}>
                                <div
                                    className="ref-logo"
                                    style={{
                                        background: `linear-gradient(135deg, ${b.color}, ${b.color}cc)`,
                                        boxShadow: `0 8px 20px -6px ${b.color}80`
                                    }}
                                    aria-label={`${b.name} logosu`}
                                >
                                    {getInitials(b.name)}
                                </div>
                                <div style={{ minWidth: 0 }}>
                                    <h3 style={{ fontWeight: 800, fontSize: '1rem', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.name}</h3>
                                    <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', fontWeight: 600 }}>{b.sector}</div>
                                </div>
                            </div>
                            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)', fontSize: '0.8125rem', fontWeight: 600 }}>
                                    <MapPin size={13} style={{ color: b.color }} /> {b.city}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)', fontSize: '0.8125rem', fontWeight: 600 }}>
                                    <Calendar size={13} style={{ color: b.color }} /> {b.since}'dan beri
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <p style={{ textAlign: 'center', color: 'var(--text-muted)', marginTop: '3rem', fontSize: '0.875rem', fontWeight: 600 }}>
                    Her hafta yeni işletmeler aramıza katılıyor.
                </p>
            </section>

            {/* Testimonials */}
            <section style={{ padding: '0 10% 6rem', background: 'rgba(var(--primary-rgb), 0.02)' }}>
                <div style={{ padding: '5rem 0 0', textAlign: 'center', marginBottom: '3rem' }}>
                    <h2 style={{ fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', fontWeight: 900, letterSpacing: '-0.02em', marginBottom: '0.75rem' }}>Müşterilerimiz ne diyor?</h2>
                    <p style={{ color: 'var(--text-muted)', fontSize: '1rem' }}>Gerçek işletme sahiplerinden gerçek yorumlar.</p>
                </div>
                <div className="testimonial-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem', maxWidth: 1200, margin: '0 auto' }}>
                    {testimonials.map((t, i) => (
                        <div key={i} className="testimonial-card">
                            <Quote size={32} style={{ color: 'var(--primary)', opacity: 0.3, marginBottom: '1rem' }} />
                            <div style={{ display: 'flex', gap: 2, marginBottom: '1rem' }}>
                                {Array.from({ length: t.rating }).map((_, i) => (
                                    <Star key={i} size={16} fill="#f59e0b" color="#f59e0b" />
                                ))}
                            </div>
                            <p style={{ color: 'var(--text-main)', fontSize: '0.9375rem', lineHeight: 1.7, marginBottom: '1.5rem', fontWeight: 500 }}>
                                "{t.quote}"
                            </p>
                            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
                                <div style={{ fontWeight: 800, fontSize: '0.9375rem' }}>{t.author}</div>
                                <div style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', fontWeight: 600 }}>{t.role}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section style={{ padding: '4rem 10% 6rem', textAlign: 'center' }}>
                <div style={{ background: 'linear-gradient(135deg, var(--primary), #5a3e2a)', borderRadius: 32, padding: '4rem 2rem', color: 'white', maxWidth: 900, margin: '0 auto', boxShadow: '0 30px 60px -20px rgba(111, 78, 55, 0.4)' }}>
                    <h2 style={{ fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', fontWeight: 900, letterSpacing: '-0.02em', marginBottom: '1rem' }}>
                        Sıra sizde
                    </h2>
                    <p style={{ fontSize: '1.0625rem', opacity: 0.9, marginBottom: '2rem', maxWidth: 560, margin: '0 auto 2rem' }}>
                        Erken dönem bir platformuz ve büyüyen bir topluluğa katılma fırsatınız var. Ücretsiz planla bugün başlayın, 10 dakikada kurulum tamamlansın.
                    </p>
                    <Link
                        to="/signup"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '1rem 2rem',
                            background: 'white',
                            color: 'var(--primary)',
                            borderRadius: 16,
                            textDecoration: 'none',
                            fontWeight: 800,
                            fontSize: '1rem',
                            transition: 'transform 0.2s',
                            boxShadow: '0 10px 25px -5px rgba(0,0,0,0.2)'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
                    >
                        Ücretsiz Başla <ArrowRight size={18} />
                    </Link>
                </div>
            </section>

            <SiteFooter />
        </div>
    );
};

export default References;
